import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useHookstate, type State } from '@hookstate/core';
import { useLocation } from 'react-router-dom';
import type ScreenSettingsDraggableProps from '~/features/Entry/interfaces/IPositionDraggable';
import { storeScreenSettings } from '~/features/System/services';
import useSystemState from '~/features/System/stores/SystemState';
import { useLang } from '~/hooks/useLang';
import { authFetch } from '~/services/fetch';
import StandardModal from '~/components/StandardModal';
import { InsertDriveFile } from '@mui/icons-material';
import { Box, Chip, Grid, Stack, Tooltip, Typography } from '@mui/material';
import useDispatchState from '../../stores/DispatchState';
import DispatchTravelInfo from '../DispatchFormModalDraggable/Components/DispatchTravelInfo';
import DispatchModalLocation from '../DispatchFormModalDraggable/Components/DispatchModalLocation';
import DispatchModalInfo from '../DispatchFormModalDraggable/Components/DispatchModalInfo';
import { Validation as validation } from '@hookstate/validation';
import { dateTimeFormat } from '~/utils/dateTime';
import { elapsedTime } from '../DispatchFormModalDraggable';
import DispatchViewMode from '../DispatchViewMode';


const modal = document.getElementById('div-modal') as HTMLElement;

export default function DispatchViewModeModalDraggable() {
  const { translate } = useLang();
  const { getStatusColors, windowManager } = useSystemState();
  const location = useLocation();
  const { dispatchHistoric } = useDispatchState();
  const { id, code, type, subtype, status, occurrence, dispatchGroup, dispatchedAt } = dispatchHistoric();
  const title = `${validation(id).valid() ? `${translate('OC')} ${occurrence?.id?.get()} - ${code.get()}` : ''}`
  const loadingModalDraggabale = useHookstate(true)
  const getPositionsDraggable = useHookstate<ScreenSettingsDraggableProps>({
    id: 0,
    user_id: 0,
    url: '',
    window_name: '',
    x_position: 80,
    y_position: 141,
    is_window_open: true,
    is_window_minimized: false,
    created_at: new Date().toJSON(),
    updated_at: new Date().toJSON(),
    deleted_at: null
  })


  const handleStop = (event, dragElement) => {
    event.stopPropagation();
    windowManager()['dispatchViewMode'].merge({
      positionX: dragElement.x,
      positionY: dragElement.y
    })
  };


  useEffect(() => {
    authFetch({
      url: '/screen-setting/get-user-settings',
      method: 'POST',
      data: {
        url: location.pathname,
      }
    }).then((response) => {
      if ('DispatchViewModeModalDraggable' in response.data) {
        getPositionsDraggable.set(response.data.DispatchViewModeModalDraggable)
      }
    }).finally(() => {
      loadingModalDraggabale.set(false)
    })

    return () => {
      windowManager()['dispatchViewMode'].readonly.set(false)
    }
  }, [])

  useEffect(() => {
    return () => {
      storeScreenSettings({
        windowName: 'DispatchViewModeModalDraggable',
        isOpen: windowManager()['dispatchViewMode'].open.get(),
        isMinimized: windowManager()['dispatchViewMode'].minimized.get(),
        positionX: windowManager()['dispatchViewMode'].positionX.get(),
        positionY: windowManager()['dispatchViewMode'].positionY.get()
      })

    }
  }, [
    windowManager()['dispatchViewMode'].open.get(),
    windowManager()['dispatchViewMode'].minimized.get(),
    windowManager()['dispatchViewMode'].positionY.get(),
    windowManager()['dispatchViewMode'].positionX.get()
  ])

  if (windowManager()['dispatchViewMode'].open.value &&
    location.pathname === '/dispatch/historic-map' &&
    !loadingModalDraggabale.get()) {
    return ReactDOM.createPortal(
      <StandardModal
        title={
          status.get() && status.id.get() ? (
            <Grid container sx={{ flexDirection: 'row', alignItems: 'center' }}>
              <Grid item sx={{ alignSelf: 'flex-start', marginRight: 'auto' }}>
                <Typography>
                  {title}
                </Typography>
                <Typography fontSize={14}>
                  {dispatchGroup.get()?.name ?? ''}
                  {' '}
                  {occurrence?.operation?.id?.get() ? `| ${translate('Operation')}: ${occurrence?.operation?.name?.get()}` : ''}
                </Typography>
              </Grid>

              <Grid item sx={{ alignSelf: 'flex-end', display: 'inline-flex', alignItems: 'center', padding: '5px', gap: 1 }}>

                {occurrence?.calls?.get() > 1 &&
                  <Stack direction='row' alignItems='center' gap={1} >
                    <Tooltip title={translate('Number of recalls')} placement='top'>
                      <Chip variant={'outlined'} label={`${translate('Recalls')}: ${occurrence?.calls?.get() as number - 1} `} />
                    </Tooltip>
                  </Stack>
                }

                <Stack direction='row' alignItems='center' gap={1} >
                  <Tooltip title={`Despachado em: ${dispatchedAt.get() ? dateTimeFormat(dispatchedAt.get()?.toString()) : ''}`} placement='top'>
                    <Chip variant={'outlined'} label={elapsedTime((dispatchedAt.get() ? dispatchedAt.get()?.toString() : ''))} />
                  </Tooltip>
                </Stack>

                <Chip
                  sx={{
                    textAlign: 'center',
                    backgroundColor: getStatusColors(status.id.get()).main,
                    color: getStatusColors(status.id.get()).contrastText,
                  }} label={status?.name.get()} />
              </Grid>

              {/* <Grid item sx={{ alignSelf: 'flex-end', display: 'inline-flex', alignItems: 'center', padding: '5px' }}>
                  </Grid> */}

              <Grid item sx={{ alignSelf: 'flex-end', display: 'inline-flex', alignItems: 'center', padding: '5px' }}>
                <DispatchTravelInfo
                  title='Locate on the map'
                  latitude={occurrence?.latitude?.get()}
                  longitude={occurrence?.longitude.get()}
                />
              </Grid>

              {Boolean(windowManager()['dispatch'].minimized.get()) &&
                <Grid item sx={{ alignSelf: 'flex-end', display: 'inline-flex', alignItems: 'center', padding: '5px' }}>
                  <DispatchModalLocation
                    latitude={occurrence.get().latitude}
                    longitude={occurrence.get().longitude}
                    modalIsExpanded={windowManager()['dispatch'].minimized.get()}
                  />
                </Grid>
              }

              <Grid item sx={{ alignSelf: 'flex-end', display: 'inline-flex', alignItems: 'center', padding: '5px' }}>
                <DispatchModalInfo
                  title='Resume'
                  code={code.get()}
                  latitude={occurrence?.latitude.get()}
                  longitude={occurrence?.longitude.get()}
                  location={occurrence?.location.get()}
                  statusName={status?.name.get()}
                  typeName={type?.name.get()}
                  subtypeName={subtype?.name.get()}
                />
              </Grid>

            </Grid>
          ) : <Typography>
            {title}
          </Typography>
        }
        handleStop={handleStop}
        avatar={InsertDriveFile}
        lat={occurrence ? (occurrence.latitude ? occurrence.latitude.get() : null) : null}
        lng={occurrence ? (occurrence.longitude ? occurrence.longitude.get() : null) : null}
        dimensions={{ width: 1000 }}
        position={{
          x: windowManager()['dispatchViewMode'].positionX.get() ? Number(windowManager()['dispatchViewMode'].positionX.get()) : 600,
          y: windowManager()['dispatchViewMode'].positionY.get() ? Number(windowManager()['dispatchViewMode'].positionY.get()) : 100
        }}
        isExpanded={windowManager()['dispatchViewMode'].minimized}
        closeButton={() => windowManager()['dispatchViewMode'].open.set(false)}
      >
        <DispatchViewMode />
      </StandardModal>
      , modal)
  } else {
    return (
      <></>
    )
  }
}